import React, { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import { useSEO } from '../../hooks/useSEO';

const NFCvsQRCodes = () => {
  useSEO({
    title: "NFC vs QR Codes: Which is Better for Business Cards? | AM CARDMATE Blog",
    description: "Compare NFC technology vs QR codes for digital business cards. Learn the pros, cons, and best use cases for each technology.",
    image: "/assets/qrcode.jpg",
    keywords: "NFC vs QR codes, comparison, digital business cards, contactless technology, which is better"
  });

  useEffect(() => {
    const schemas = [
      {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": "NFC vs QR Codes: Which is Better for Business Cards?",
        "description": "Compare NFC technology vs QR codes for digital business cards",
        "datePublished": "2025-06-05",
        "dateModified": "2025-06-05",
        "author": { "@type": "Person", "name": "Alina Macovei", "jobTitle": "Founder & CEO", "worksFor": { "@type": "Organization", "name": "AM CARDMATE" } },
        "publisher": { "@type": "Organization", "name": "AM CARDMATE", "logo": { "@type": "ImageObject", "url": "https://amcardmate.com/assets/logo.svg" } },
        "image": "https://amcardmate.com/assets/qrcode.jpg",
        "mainEntityOfPage": { "@type": "WebPage", "@id": "https://amcardmate.com/blog/nfc-vs-qr-codes" }
      }
    ];
    schemas.forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });
  }, []);

  const rounds = [
    { num: 1, label: 'Speed & Convenience', color: 'text-orange-500', nfc: { emoji: '💨', score: '10/10', text: "Literally just tap. That's it. Your grandma could do it blindfolded. Time to share: 0.1 seconds." }, qr: { emoji: '📸', score: '6/10', text: 'Open camera, aim, wait for focus, click the popup. Hope the lighting is good. Time: 5-10 seconds.' }, winner: 'NFC' },
    { num: 2, label: 'User Experience', color: 'text-purple-500', nfc: { emoji: '🎯', score: '9/10', text: "No app needed, no camera permissions, works in the dark, works when you're tipsy at networking events. Pure magic." }, qr: { emoji: '🎪', score: '5/10', text: '"Is it blurry? Let me get closer. Wait, too close. Is this the right angle?" Plus, everyone sees you awkwardly pointing your phone.' }, winner: 'NFC' },
    { num: 3, label: 'The Cool Factor', color: 'text-pink-500', nfc: { emoji: '😎', score: '10/10', text: '"Just tap my card." *Mind blown* reactions every time. You\'ll feel like Tony Stark sharing his contact.' }, qr: { emoji: '📱', score: '3/10', text: "About as exciting as scanning a restaurant menu. We all do it, but nobody's impressed anymore." }, winner: 'NFC (by knockout!)' },
    { num: 4, label: 'Compatibility', color: 'text-green-500', nfc: { emoji: '📱', score: '9/10', text: "Works on 99% of modern smartphones. If your phone was made after 2015, you're golden." }, qr: { emoji: '📷', score: '10/10', text: 'Works on literally anything with a camera. Even that Nokia from 2010 (with the right app).' }, winner: 'QR Code (finally!)' }
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="pt-32 md:pt-36 lg:pt-40 pb-20 bg-gradient-to-b from-green-50 via-teal-50/20 to-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-br from-green-200/20 to-teal-200/20 rounded-full filter blur-3xl" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto">
            <nav className="flex flex-wrap items-center gap-2 text-caption mb-6">
              <Link to="/" className="hover:text-primary-blue transition-colors">Home</Link>
              <FontAwesomeIcon icon="chevron-right" className="text-xs" />
              <Link to="/blog" className="hover:text-primary-blue transition-colors">Blog</Link>
              <FontAwesomeIcon icon="chevron-right" className="text-xs" />
              <span className="text-primary-blue">NFC vs QR Codes</span>
            </nav>

            <div className="text-center mb-8">
              <div className="badge badge-green inline-flex mb-6">
                <FontAwesomeIcon icon="balance-scale" />
                <span>Technology Comparison</span>
              </div>

              <h1 className="text-h1 mb-6">
                NFC vs QR Codes:
                <span className="block text-gradient-blue-pink mt-2">The Ultimate Showdown</span>
              </h1>

              <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 text-caption">
                <span className="flex items-center gap-2">
                  <FontAwesomeIcon icon="user" className="text-primary-blue" />
                  Alina Macovei
                </span>
                <span className="flex items-center gap-2">
                  <FontAwesomeIcon icon="calendar" className="text-primary-blue" />
                  June 5, 2025
                </span>
                <span className="flex items-center gap-2">
                  <FontAwesomeIcon icon="clock" className="text-primary-blue" />
                  6 min read
                </span>
              </div>
            </div>

            <div className="relative rounded-3xl overflow-hidden shadow-2xl mb-12">
              <img src="https://images.unsplash.com/photo-1595079676339-1534801ad6cf?w=1200&h=600&fit=crop" alt="NFC vs QR Code Technology Comparison" className="w-full h-auto" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="bg-white/90 backdrop-blur-sm rounded-full w-24 h-24 md:w-32 md:h-32 flex items-center justify-center">
                  <span className="text-3xl md:text-4xl font-extrabold text-gradient-blue-pink">VS</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <p className="text-body-lg text-gray-600 mb-8">
              It's the tech battle of the century! In the red corner, we have QR codes – the pixelated squares that became everyone's pandemic buddy. In the blue corner, NFC technology – the tap-and-go champion of the digital age. Who wins in the world of digital business cards? Let's find out!
            </p>

            <div className="bg-gradient-to-r from-green-50 to-teal-50 rounded-2xl p-6 md:p-8 mb-12">
              <h3 className="text-h4 text-dark mb-4 flex items-center gap-3">
                <FontAwesomeIcon icon="trophy" className="text-yellow-500" />
                Spoiler Alert
              </h3>
              <p className="text-body text-gray-600">
                While both technologies have their place, NFC wins for business cards by a landslide. But don't worry, we'll give QR codes a fair fight. We're not biased... much.
              </p>
            </div>

            {/* Meet the Contenders */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-teal-600 rounded-xl flex items-center justify-center text-white">
                  <FontAwesomeIcon icon="users" />
                </div>
                <h2 className="text-h3 text-dark">Meet the Contenders</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-6 hover:shadow-lg transition-all">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-purple-600 rounded-xl flex items-center justify-center text-white">
                      <FontAwesomeIcon icon="wifi" className="text-xl" />
                    </div>
                    <h3 className="text-h5 text-dark">Team NFC</h3>
                  </div>
                  <ul className="space-y-3 text-body text-gray-600">
                    {['Born: 2002 (but stayed cool)', 'Superpower: Tap and go magic', 'Best friend: Your smartphone', 'Motto: "Why scan when you can tap?"'].map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <FontAwesomeIcon icon="check" className="text-green-500 mt-1 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-6 hover:shadow-lg transition-all">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-gray-600 to-gray-800 rounded-xl flex items-center justify-center text-white">
                      <FontAwesomeIcon icon="qrcode" className="text-xl" />
                    </div>
                    <h3 className="text-h5 text-dark">Team QR Code</h3>
                  </div>
                  <ul className="space-y-3 text-body text-gray-600">
                    {['Born: 1994 (the veteran)', 'Superpower: Works on any camera', 'Best friend: Restaurant menus', 'Motto: "Square is the new cool"'].map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <FontAwesomeIcon icon="check" className="text-gray-500 mt-1 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Round-by-Round */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-red-600 rounded-xl flex items-center justify-center text-white">
                  <FontAwesomeIcon icon="fire" />
                </div>
                <h2 className="text-h3 text-dark">Round-by-Round Breakdown</h2>
              </div>

              <div className="space-y-6">
                {rounds.map((round, i) => (
                  <div key={i} className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all">
                    <h3 className="text-h5 text-dark mb-4">
                      <span className={round.color}>Round {round.num}:</span> {round.label}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="text-body font-bold text-blue-600 mb-2">NFC {round.nfc.emoji}</h4>
                        <p className="text-body text-gray-600 mb-3">{round.nfc.text}</p>
                        <div className="bg-blue-100 rounded-lg p-3 text-blue-700 font-medium text-body-sm">Score: {round.nfc.score}</div>
                      </div>
                      <div>
                        <h4 className="text-body font-bold text-gray-600 mb-2">QR Code {round.qr.emoji}</h4>
                        <p className="text-body text-gray-600 mb-3">{round.qr.text}</p>
                        <div className="bg-gray-100 rounded-lg p-3 text-gray-700 font-medium text-body-sm">Score: {round.qr.score}</div>
                      </div>
                    </div>
                    <div className="mt-4 text-center">
                      <span className="text-body font-bold text-green-600">🏆 Winner: {round.winner}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Final Verdict */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-xl flex items-center justify-center text-white">
                  <FontAwesomeIcon icon="gavel" />
                </div>
                <h2 className="text-h3 text-dark">The Final Verdict</h2>
              </div>

              <div className="bg-gradient-to-br from-white to-gray-50 rounded-3xl p-6 md:p-8 shadow-xl">
                <div className="text-center mb-8">
                  <div className="inline-flex items-center gap-4 flex-wrap justify-center">
                    <div className="text-center">
                      <div className="text-h1 text-primary-blue mb-2">NFC</div>
                      <div className="text-h3 text-gray-700">39/40</div>
                    </div>
                    <span className="text-h2 text-gray-400">vs</span>
                    <div className="text-center">
                      <div className="text-h1 text-gray-600 mb-2">QR</div>
                      <div className="text-h3 text-gray-700">24/40</div>
                    </div>
                  </div>
                </div>

                <p className="text-body-lg text-gray-600 mb-6 text-center max-w-3xl mx-auto">
                  For digital business cards, NFC is the clear champion. It's faster, cooler, and creates a memorable experience. QR codes are great for restaurant menus and WiFi passwords, but when it comes to making a professional impression? NFC takes the crown.
                </p>

                <div className="bg-gradient-to-r from-primary-blue/10 to-accent-purple/10 rounded-xl p-6 mt-8">
                  <h3 className="text-h5 text-dark mb-3 text-center">But Wait... AM CARDMATE Has Both!</h3>
                  <p className="text-body text-gray-600 text-center">
                    Here's the plot twist: Every AM CARDMATE NFC card comes with a QR code backup. Because we believe in having options. It's like having Batman AND Superman on your team.
                  </p>
                </div>
              </div>
            </div>

            {/* When to Use Each */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-teal-600 rounded-xl flex items-center justify-center text-white">
                  <FontAwesomeIcon icon="lightbulb" />
                </div>
                <h2 className="text-h3 text-dark">The Smart Choice: When to Use Each</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-6">
                  <h3 className="text-card-title text-dark mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="wifi" className="text-blue-600" />
                    Use NFC When:
                  </h3>
                  <ul className="space-y-2 text-body text-gray-600">
                    {['Meeting someone in person', 'You want to make an impression', 'Speed matters (conferences, events)', 'You want that "wow" factor'].map((item, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <FontAwesomeIcon icon="check-circle" className="text-green-500 mt-1" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-6">
                  <h3 className="text-card-title text-dark mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="qrcode" className="text-gray-600" />
                    Use QR When:
                  </h3>
                  <ul className="space-y-2 text-body text-gray-600">
                    {["Someone's NFC is turned off", 'Sharing on video calls', 'Printing on materials', 'Dealing with older phones'].map((item, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <FontAwesomeIcon icon="check-circle" className="text-gray-500 mt-1" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Fun Facts */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-gradient-to-br from-pink-400 to-pink-600 rounded-xl flex items-center justify-center text-white">
                  <FontAwesomeIcon icon="star" />
                </div>
                <h2 className="text-h3 text-dark">Fun Facts That'll Make You Sound Smart</h2>
              </div>

              <div className="space-y-4">
                {[
                  { bg: 'from-blue-50 to-purple-50', label: '🤓 NFC Fact', text: 'NFC is based on RFID technology but operates at a specific frequency (13.56 MHz). It\'s the same tech that powers your contactless credit cards!' },
                  { bg: 'from-gray-50 to-gray-100', label: '🤓 QR Code Fact', text: 'QR stands for "Quick Response" and was invented by Denso Wave (a Toyota subsidiary) to track car parts. Now it tracks your lunch orders!' },
                  { bg: 'from-green-50 to-teal-50', label: '🤓 Bonus Fact', text: "The largest QR code ever made was 177m x 177m in China. The smallest NFC chip is 0.15mm x 0.15mm. Size doesn't always matter!" }
                ].map((fact, i) => (
                  <div key={i} className={`bg-gradient-to-r ${fact.bg} rounded-2xl p-6 hover:shadow-md transition-all`}>
                    <h4 className="text-card-title text-dark mb-2">{fact.label}</h4>
                    <p className="text-body text-gray-600">{fact.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-[#4DA2FF] to-[#DE6161] relative overflow-hidden">
        <div className="absolute inset-0 bg-dots-pattern opacity-10"></div>
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center">
            <h2 className="text-h2 text-white mb-4">Experience NFC Magic</h2>
            <p className="text-body-lg text-white/90 mb-8 max-w-2xl mx-auto">
              With AM CARDMATE, you get the best of both worlds: cutting-edge NFC technology plus a QR code backup. It's like having a Ferrari with a spare tire.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/nfc-cards" className="btn bg-white text-primary-blue hover:bg-gray-100 transition-all shadow-lg">
                <FontAwesomeIcon icon="wifi" />
                <span>Experience NFC Magic</span>
              </Link>
              <Link to="/blog" className="btn bg-white/20 text-white hover:bg-white/30 transition-all">
                <FontAwesomeIcon icon="book-open" />
                <span>More Tech Insights</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Author & Related */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-white to-blue-50/30">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="border-t pt-8 mb-12">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary-blue to-accent-purple flex items-center justify-center text-white font-bold text-body-lg flex-shrink-0">AM</div>
                <div>
                  <h3 className="text-card-title text-dark">Alina Macovei</h3>
                  <p className="text-body-sm text-gray-500 mb-2">Founder & CEO, AM CARDMATE</p>
                  <p className="text-body-sm text-gray-600">
                    Tech enthusiast who believes the best technology is the one that feels like magic. When not evangelizing NFC, I can be found tapping my card on random objects to see what happens (spoiler: usually nothing, but it's fun to try!).
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-h3 text-dark mb-6">Keep Reading</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                {[
                  { to: '/blog/what-is-nfc-technology', title: 'What is NFC Technology?', gradient: 'from-blue-400 to-blue-600' },
                  { to: '/blog/how-nfc-business-cards-work', title: 'How NFC Business Cards Work', gradient: 'from-purple-400 to-pink-600' },
                  { to: '/blog/future-of-networking', title: 'The Future of Networking', gradient: 'from-purple-500 to-pink-500' }
                ].map((post, i) => (
                  <Link key={i} to={post.to} className={`group ${i === 2 ? 'sm:col-span-2 md:col-span-1' : ''}`}>
                    <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300">
                      <div className={`h-40 bg-gradient-to-br ${post.gradient}`}></div>
                      <div className="p-4">
                        <h4 className="text-body font-bold text-dark group-hover:text-primary-blue transition-colors">{post.title}</h4>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default NFCvsQRCodes;